import {createContext, useReducer, ReactElement, Reducer, useEffect, Dispatch, FC, useContext, useState} from "react";
import {IModuleScheduled} from "@buildwitt/shapeshift-types";
import {useOverdueModules} from "@buildwitt/shapeshift-common/v1/hooks";
import {AuthContext, useApi} from "@buildwitt/shapeshift-identity";
import {LocaleContext} from "./localeContext";
import {App} from "@capacitor/app";
import useCustomScheduledModules from "@/hooks/UseCustomScheduledModules";
import {SocketContext} from "./socketContext";
import useDevCycleSdk from "@/hooks/useDevCycleSdk";

const getToday = (): Date => {
	const date = new Date();
	date.setHours(0, 0, 0, 0);
	return date;
};

const getTomorrow = (): Date => {
	const date = new Date();
	date.setDate(date.getDate() + 1);
	date.setHours(0, 0, 0, 0);
	return date;
};

const getNext = (): Date => {
	const date = new Date();
	date.setDate(date.getDate() + 2);
	date.setHours(0, 0, 0, 0);
	return date;
};

const initialState: {
	trainingfeedState?: TrainingfeedContextState;
	trainingfeedDispatch: Dispatch<TrainingfeedContextAction>;
	refreshTrainingFeed?: () => void;
	refreshHomePage: number;
} = {
	trainingfeedState: undefined,
	trainingfeedDispatch: () => {},
	refreshTrainingFeed: undefined,
	refreshHomePage: 0
};

const TrainingfeedContext = createContext(initialState);

interface ITrainingFeed {[day: string | "overdue"]: (IModuleScheduled[] | "loading" | "errored")}

type TrainingfeedContextState = ITrainingFeed | undefined

type TrainingfeedContextAction = {
	type: "set";
	payload?: ITrainingFeed;
} | {
	type: "getDay",
	day: Date
} | {
	type: "setDay",
	day: Date,
	modules: IModuleScheduled[]
} | {
	type: "setOverdue",
	modules: IModuleScheduled[]
} | {
	type: "setModuleComplete",
	module: {courseSlug: string, type: string, slug: string},
	status: boolean
} | {
	type: "setDayErrored",
	day: Date
}

interface IProps {
	children: ReactElement
}


const TrainingfeedProvider: FC<IProps> = ({children}) => {
	const socket = useContext(SocketContext);
	const [{locale}] = useContext(LocaleContext);
	const {newHomeScreen} = useDevCycleSdk();

	//By Default get overdue, today, tomorrow
	const {overdueModules, getOverdueModules} = useOverdueModules(locale);
	// const {scheduledModules: todayModules} = useScheduledModules(today, tomorrow);
	const {authenticated} = useContext(AuthContext)

	const [trackedDay, setTrackedDay] = useState<number>(() => getToday().getTime())

	//initial call to additional is for tomorrow
	// const {scheduledModules: additionalModules, getScheduledModules, apiResult: additionalModulesApiResult} = useScheduledModules(today, tomorrow, locale);
	const {scheduledModules: additionalModules, getScheduledModules, apiResult: additionalModulesApiResult} = useCustomScheduledModules();
	const [dayRequested, setDayRequested] = useState<number>();
	const [trainingfeedUpdatedAt, getTrainingfeedUpdatedAt] = useApi<Date>("/api/user/trainingfeed/updatedAt");
	const [lastTrainingFeedCall, setLastTrainingFeedCall] = useState<Date>();
	const [renewTrainingFeedResponse, renewTrainingFeed] = useApi<boolean>();

	const [refreshHomePage, setRefreshHomePage] = useState(0);

	const triggerHomePageRefresh = (): void => {
		setRefreshHomePage(prev => prev + 1);
	};

	useEffect(() => {
		if (socket) {
			socket.on("connect", () => {
				if (socket.listeners("trainingFeedUpdatedAtEvent").length === 0){
					socket.on("trainingFeedUpdatedAtEvent", handleTrainingFeedUpdatedAtEvent);
				}
			});

			return () => {
				if (socket.listeners("trainingFeedUpdatedAtEvent").length > 0){
					socket.off("trainingFeedUpdatedAtEvent");
				}
			}
		}
	}, [socket]);

	const handleTrainingFeedUpdatedAtEvent = (e: { data: { trainingFeedUpdatedAt: string; }}) => {
		if(newHomeScreen) {
			triggerHomePageRefresh();
		}
		else
			refreshTrainingFeed();
	}

	useEffect(() => {
		const interval = setInterval(() => {
			getTrainingfeedUpdatedAt();

			const currentDay = getToday().getTime();
			if (currentDay !== trackedDay) {
				setTrackedDay(currentDay);
				if(newHomeScreen) {
					triggerHomePageRefresh();
				}
				else
					refreshTrainingFeed();
			}
		}, 30000);

		return () => clearInterval(interval);
	}, [trackedDay]);

	useEffect(() => {
		if(!trainingfeedUpdatedAt.data || trainingfeedUpdatedAt.isLoading)
			return;

		if(new Date(trainingfeedUpdatedAt.data).getTime() > lastTrainingFeedCall?.getTime())
			if(newHomeScreen) {
				triggerHomePageRefresh();
			}
			else
				refreshTrainingFeed();

	}, [trainingfeedUpdatedAt])

	const [trainingFeedState, trainingfeedDispatch] = useReducer<Reducer<TrainingfeedContextState, TrainingfeedContextAction>>((state: TrainingfeedContextState, action: TrainingfeedContextAction) => {
		switch (action.type) {
			case "set": {
				return {...action.payload};
			}
			case "getDay": {
				action.day.setHours(0, 0, 0, 0);
				if(!state)
					state = {};

				state[action.day.getTime()] = "loading"
				return {...state};
			}
			case "setDay": {
				if(!state)
					state = {};
				action.day.setHours(0, 0, 0, 0)
				state[action.day.getTime()] = action.modules
				return {...state};
			}
			case "setOverdue": {
				if(!state)
					state = {};
				state["overdue"] = action.modules
				return {...state};
			}
			case "setModuleComplete": {
				if(!state)
					state = {};

				//get module
				for(const day of Object.keys(state)){
					for(const module of state[day]){
						if(
							typeof module !== "string" &&
							module.course.slug === action.module.courseSlug &&
							module.type === action.module.type &&
							module.slug === action.module.slug)
						{
							module.complete = action.status;
						}
					}
				}
				return {...state};
			}
			case "setDayErrored": {
				if(!state)
					state = {};
				state[action.day.getTime()] = "errored"
				return state;
			}
			default:
				return state;
		}
	}, initialState.trainingfeedState);

	useEffect(() => {
		if(!newHomeScreen) {
			refreshTrainingFeed();
		}
	}, [authenticated, locale])

	useEffect(() => {
		if(additionalModules){
			const day = dayRequested ? new Date(dayRequested) : getToday();
			trainingfeedDispatch({type: "setDay", day, modules: additionalModules});
		}
	}, [additionalModules])

	useEffect(() => {
		if(overdueModules){
			trainingfeedDispatch({type: "setOverdue", modules: overdueModules});
		}
	}, [overdueModules])

	useEffect(() => {
		if(additionalModulesApiResult.isLoading)
			return;

		if(additionalModulesApiResult.isError)
			trainingfeedDispatch({type: "setDayErrored", day: dayRequested ? new Date(dayRequested) : getToday()});

		//look for next day
		if(trainingFeedState){

			const nextDay = Object.keys(trainingFeedState ?? {}).sort((a,b) => {
				if (a === "overdue") return -1;
				if (b === "overdue") return 1;
				return Number(a) - Number(b);
			})?.filter(e => e !== "overdue" && trainingFeedState?.[e] === "loading")?.[0]

			if(!nextDay)
				return;

			const dayRequested = new Date(parseInt(nextDay));
			if(!dayRequested)
				return;

			setDayRequested(dayRequested.getTime())
		}
	}, [trainingFeedState, additionalModulesApiResult])

	useEffect(() => {
		if(!dayRequested)
			return;

		const day = new Date(dayRequested);
		const endDate = new Date(dayRequested);
		endDate.setDate(endDate.getDate() + 1);
		getScheduledModules(day, endDate, locale, {throttle: false})
	}, [dayRequested])

	const refreshTrainingFeed = (): void => {
		if(!authenticated)
			return;

		const today = getToday();
		const tomorrow = getTomorrow();
		const next = getNext();

		trainingfeedDispatch({type: "set", payload: {
			"overdue": "loading",
			[today.getTime()]: "loading",
			[tomorrow.getTime()]: "loading",
			[next.getTime()]: "loading"
		}});

		setDayRequested(today.getTime());
		if (lastTrainingFeedCall && !newHomeScreen) {
			getOverdueModules(locale, {cache: false});
		}
		setLastTrainingFeedCall(new Date());
	}

	useEffect(() => {
		const unsubscribe = App.addListener("appStateChange", ({isActive}) => {
			if (isActive && authenticated) {
				if(newHomeScreen) {
					triggerHomePageRefresh();
				}
				else
					refreshTrainingFeed();
			}
		});

		return () => {
			unsubscribe.remove();
		};
	}, [authenticated, locale]);

	useEffect(() => {
		if(!trainingFeedState)
			return;

		//check if trainingFeedState is empty or 1 off empty
		const kets = Object.keys(trainingFeedState);
		const isLoading = kets.some(key => trainingFeedState[key] === "loading");

		if(isLoading)
			return;

		const moduleCount = kets.reduce((count, key) => {
			return count + (Array.isArray(trainingFeedState[key]) ? (trainingFeedState[key] as IModuleScheduled[]).filter(e => !e.complete).length : 0);
		}, 0);

		if(moduleCount < 2){
			renewTrainingFeed({url: "/api/user/refreshTrainingFeed", method: "PUT"})
		}

	}, [trainingFeedState]);

	return (
		<TrainingfeedContext.Provider value={{
			trainingfeedState: trainingFeedState,
			trainingfeedDispatch: trainingfeedDispatch,
			refreshTrainingFeed: refreshTrainingFeed,
			refreshHomePage: refreshHomePage
		}}>
			{children}
		</TrainingfeedContext.Provider>
	);
};

export {
	TrainingfeedContext,
	TrainingfeedProvider
};